import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  Plus, 
  Trophy, 
  Users, 
  Calendar, 
  LogOut, 
  User,
  ChevronRight,
  Target,
  Shield,
  Image as ImageIcon,
  Crown,
  CalendarDays,
  Check
} from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';
import { RedeemDrinkButton } from '@/components/RedeemDrinkButton';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { member, logout } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSeason, setActiveSeason] = useState(null);
  const [seasonRegistered, setSeasonRegistered] = useState(false);
  const [registeringForSeason, setRegisteringForSeason] = useState(false);

  useEffect(() => {
    fetchTournaments();
    fetchActiveSeason();
  }, []);

  const fetchActiveSeason = async () => {
    try {
      const response = await axios.get(`${API_URL}/seasons/active`);
      if (response.data) {
        setActiveSeason(response.data);
        if (member?.id) {
          const regCheck = await axios.get(`${API_URL}/seasons/${response.data.id}/check-registration?member_id=${member.id}`);
          setSeasonRegistered(regCheck.data.registered);
        }
      }
    } catch (error) {
      // No active season
    }
  };

  const handleSeasonRegister = async () => {
    if (!activeSeason || !member?.id) return;
    setRegisteringForSeason(true);
    try {
      await axios.post(`${API_URL}/seasons/${activeSeason.id}/register?member_id=${member.id}`);
      setSeasonRegistered(true);
      toast.success(`Registered for ${activeSeason.name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register');
    } finally {
      setRegisteringForSeason(false);
    }
  };

  const fetchTournaments = async () => {
    try {
      const response = await axios.get(`${API_URL}/tournaments`);
      setTournaments(response.data);
    } catch (error) {
      toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'status-open',
      in_progress: 'status-in-progress',
      completed: 'status-completed',
      closed: 'bg-yellow-500/20 text-yellow-500 border border-yellow-500',
      archived: 'bg-gray-500/20 text-gray-500 border border-gray-500'
    };
    const labels = {
      open: 'Open',
      in_progress: 'In Progress',
      completed: 'Completed',
      closed: 'Closed',
      archived: 'Archived'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.completed}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getTournamentTypeBadge = (type) => {
    if (type === 'pro') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border border-amber-500/50">
          <Crown className="w-3 h-3" />
          Pro Tour
        </span>
      );
    }
    return null;
  };

  const canJoinTournament = (tournament) => {
    if (tournament.tournament_type === 'pro') {
      return member?.membership_tier === 'pro';
    }
    return true;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Sponsor Banner - Top of Page */}
      <SponsorBanner variant="top-banner" />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img 
              src={TGA_LOGO} 
              alt="TGA Pro Tour" 
              className="h-10 object-contain cursor-pointer"
              onClick={() => navigate('/')}
              data-testid="nav-logo"
            />
            <div className="hidden sm:flex items-center gap-1">
              <Button
                variant="ghost"
                className="text-white text-sm border-b-2 border-primary rounded-none"
                data-testid="nav-dashboard-active"
              >
                Dashboard
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/schedule')}
                className="text-muted-foreground hover:text-white text-sm"
                data-testid="nav-schedule"
              >
                Schedule
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/tours')}
                className="text-muted-foreground hover:text-white text-sm"
                data-testid="nav-tours"
              >
                Tours
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/rankings')}
                className="text-muted-foreground hover:text-white text-sm"
                data-testid="nav-rankings"
              >
                Rankings
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {member?.is_admin && (
              <span className="px-2 py-1 rounded bg-primary/20 text-primary text-xs font-medium border border-primary/50">
                Admin
              </span>
            )}
            <Button 
              variant="ghost"
              onClick={() => navigate('/profile')}
              className="text-muted-foreground hover:text-white"
              data-testid="nav-profile-btn"
            >
              <User className="mr-2 h-4 w-4" />
              {member?.name}
            </Button>
            <Button 
              variant="ghost"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-destructive"
              data-testid="nav-logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading text-white">
              Welcome, {member?.name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground mt-1">
              Member #{member?.member_number}
            </p>
          </div>
          {member?.is_admin && (
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={() => navigate('/admin/members')}
                variant="outline"
                className="border-primary/50 hover:bg-primary/20 rounded-pill px-6"
                data-testid="manage-members-btn"
              >
                <Shield className="mr-2 h-4 w-4" />
                Manage Members
              </Button>
              <Button 
                onClick={() => navigate('/admin/sponsors')}
                variant="outline"
                className="border-primary/50 hover:bg-primary/20 rounded-pill px-6"
                data-testid="manage-sponsors-btn"
              >
                <ImageIcon className="mr-2 h-4 w-4" />
                Manage Sponsors
              </Button>
              <Button 
                onClick={() => navigate('/admin/seasons')}
                variant="outline"
                className="border-primary/50 hover:bg-primary/20 rounded-pill px-6"
                data-testid="manage-seasons-btn"
              >
                <CalendarDays className="mr-2 h-4 w-4" />
                Manage Seasons
              </Button>
              {member?.is_super_admin && (
                <Button 
                  onClick={() => navigate('/admin/approvals')}
                  variant="outline"
                  className="border-amber-500/50 hover:bg-amber-500/20 text-amber-400 rounded-pill px-6"
                  data-testid="manage-approvals-btn"
                >
                  <Shield className="mr-2 h-4 w-4" />
                  Approvals
                </Button>
              )}
              <RedeemDrinkButton
                className="border-amber-500/50 hover:bg-amber-500/20 text-amber-300 rounded-pill px-6"
              />
              <Button 
                onClick={() => navigate('/tournaments/create')}
                className="bg-primary hover:bg-primary/90 rounded-pill px-6 btn-hover-scale glow-purple"
                data-testid="create-tournament-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Tournament
              </Button>
            </div>
          )}
        </div>

        {/* Active Season Banner */}
        {activeSeason && (
          <Card className="bg-card border-primary/30 mb-8 overflow-hidden" data-testid="season-banner">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <CalendarDays className="w-6 h-6 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-heading text-base">{activeSeason.name}</h3>
                    <p className="text-muted-foreground text-xs">
                      {new Date(activeSeason.start_date).toLocaleDateString()} — {new Date(activeSeason.end_date).toLocaleDateString()} &middot; {activeSeason.registered_count} registered
                    </p>
                  </div>
                </div>
                {seasonRegistered ? (
                  <span className="flex items-center gap-1.5 text-sm text-green-400 font-medium" data-testid="season-registered-badge">
                    <Check className="w-4 h-4" />
                    Registered
                  </span>
                ) : (
                  <Button
                    onClick={handleSeasonRegister}
                    disabled={registeringForSeason}
                    size="sm"
                    className="bg-primary hover:bg-primary/90 rounded-pill px-5"
                    data-testid="season-register-btn"
                  >
                    {registeringForSeason ? 'Registering...' : 'Register for Season'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Home Game Promo */}
          <a
            href="https://tablegolfassociation.com/shop/meeple-beach-pro-edition/"
            target="_blank"
            rel="noopener noreferrer"
            className="block group md:col-span-3 mb-2"
            data-testid="home-game-promo"
          >
            <div className="bg-card border border-secondary/30 hover:border-secondary/60 rounded-xl p-5 flex items-center gap-5 transition-all group-hover:shadow-lg group-hover:shadow-secondary/5">
              <img
                src={TGA_LOGO}
                alt="TGA Home Game"
                className="w-16 h-16 object-contain shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-heading text-white">Buy the Home Version!</h3>
                <p className="text-muted-foreground text-sm">
                  Click here to buy the home version of the TGA, America's favorite tabletop golf game.
                  Use code <span className="text-secondary font-semibold font-mono">TGAprotour</span> to save $10.
                </p>
              </div>
            </div>
          </a>
          <Card className="bg-card border-white/10 card-hover">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">Tournaments Played</p>
                  <p className="text-2xl font-mono font-bold text-white">
                    {member?.tournaments_played || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-white/10 card-hover">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center">
                  <Target className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">Best Score</p>
                  <p className="text-2xl font-mono font-bold text-white">
                    {member?.best_score !== null ? (
                      member.best_score === 0 ? 'E' : 
                      member.best_score > 0 ? `+${member.best_score}` : 
                      member.best_score
                    ) : '--'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-white/10 card-hover">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">Active Tournaments</p>
                  <p className="text-2xl font-mono font-bold text-white">
                    {tournaments.filter(t => t.status !== 'completed').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tournaments List */}
        <div>
          <h2 className="text-2xl font-heading text-white mb-6">Tournaments</h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-pulse text-primary">Loading tournaments...</div>
            </div>
          ) : tournaments.length === 0 ? (
            <Card className="bg-card border-white/10">
              <CardContent className="p-12 text-center">
                <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-heading text-white mb-2">No Tournaments Yet</h3>
                <p className="text-muted-foreground mb-6">
                  {member?.is_admin ? "Create your first tournament to get started!" : "Check back soon for upcoming tournaments!"}
                </p>
                {member?.is_admin && (
                  <Button 
                    onClick={() => navigate('/tournaments/create')}
                    className="bg-primary hover:bg-primary/90 rounded-pill"
                    data-testid="empty-create-btn"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Tournament
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {tournaments.map((tournament, index) => (
                <Card 
                  key={tournament.id}
                  className="bg-card border-white/10 tournament-card cursor-pointer animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => navigate(`/tournaments/${tournament.id}`)}
                  data-testid={`tournament-card-${index}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-heading text-white">
                            {tournament.name}
                          </h3>
                          {getStatusBadge(tournament.status)}
                          {getTournamentTypeBadge(tournament.tournament_type)}
                        </div>
                        <p className="text-muted-foreground text-sm mb-3">
                          {tournament.description || 'No description'}
                        </p>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Target className="w-4 h-4" />
                            {tournament.num_holes} Holes
                          </span>
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Users className="w-4 h-4" />
                            {tournament.participants_count} Players
                          </span>
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            {formatDate(tournament.created_at)}
                          </span>
                          <span className="flex items-center gap-1 text-primary font-mono">
                            Par {tournament.total_par}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-6 h-6 text-muted-foreground hidden md:block" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
